import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSaveSharedTrip, useSharedTrip } from "@/lib/api/hooks";
import { useAuthStore, useUiStore } from "@/lib/store";
import { ItineraryView } from "@/components/itinerary/ItineraryView";
import { ItineraryMap } from "@/components/itinerary/ItineraryMap";
import { SaveSharedDialog } from "@/components/share/SaveSharedDialog";
import type { Trip } from "@/lib/api/schemas";

export default function SharePage() {
  const { token } = useParams<{ token: string }>();
  const { data: trip, isLoading, error } = useSharedTrip(token);
  const account = useAuthStore((s) => s.account);
  const addNotification = useUiStore((s) => s.addNotification);
  const navigate = useNavigate();

  const [focusedItemId, setFocusedItemId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Post-login auto-save: when the user arrives via
  // /auth/login?next=/shared/:token&intent=save the login page stores
  // an "intent=save" flag in sessionStorage. On mount here, consume
  // it and re-open the save dialog automatically.
  useEffect(() => {
    if (!account || !trip || !token) return;
    const intent = window.sessionStorage.getItem("pendingSaveIntent");
    if (intent && intent === token) {
      window.sessionStorage.removeItem("pendingSaveIntent");
      setDialogOpen(true);
    }
  }, [account, trip, token]);

  // Auto-redirect: backend returns 409 if the viewer already owns the
  // source trip — open it in their own trips instead of duplicating.
  const conflictRedirect = useSaveSharedTrip(token, {
    onConflict: (existingTripId) => navigate(`/plan/${existingTripId}`),
  });

  const handleItemFocus = useCallback((id: string | null) => {
    setFocusedItemId(id);
  }, []);
  const handleItemSelect = useCallback((id: string) => {
    setSelectedItemId((prev) => (prev === id ? null : id));
  }, []);
  const handleMarkerClick = useCallback((id: string) => {
    setSelectedItemId(id);
    const el = document.getElementById(`itinerary-item-${id}`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-light">
        Loading shared trip...
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="rounded-2xl frost-card px-8 py-7 text-center">
          <h1 className="mb-2 text-2xl font-bold text-red-300">Trip not found</h1>
          <p className="text-ink-light">
            This shared link may have expired or been removed.
          </p>
        </div>
      </div>
    );
  }

  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const saveCount = trip.saveCount ?? 0;
  const authorName = trip.authorName || "";
  const handleSaved = (newTrip: Trip) => {
    setDialogOpen(false);
    addNotification({
      type: "success",
      message: "Saved to your trips — you can now edit it.",
      duration: 4000,
    });
    navigate(`/plan/${newTrip.id}`);
  };

  // Button state depends on auth + ownership.
  const renderCTA = () => {
    if (!token) return null;
    if (!account) {
      return (
        <button
          onClick={() => {
            // Persist the intent so the login page can auto-resume.
            window.sessionStorage.setItem("pendingSaveIntent", token);
            const next = encodeURIComponent(
              `/shared/${token}?intent=save`
            );
            navigate(`/auth/login?next=${next}`);
          }}
          className="rounded-xl bg-accent px-5 py-2.5 text-sm font-bold text-deep hover:bg-accent-light"
        >
          Sign in to save
        </button>
      );
    }
    return (
      <button
        onClick={() => {
          // Try an immediate save without opening the dialog — on
          // 409 the conflictRedirect callback navigates to the
          // user's existing copy. Otherwise we open the modal for
          // customization.
          if (conflictRedirect.isPending) return;
          setDialogOpen(true);
        }}
        className="rounded-xl bg-accent px-5 py-2.5 text-sm font-bold text-deep hover:bg-accent-light"
      >
        Save to my trips
      </button>
    );
  };

  const authorByline = () => {
    const parts: string[] = [];
    if (authorName) parts.push(`Shared by ${authorName}`);
    if (saveCount > 0) {
      parts.push(`${saveCount} save${saveCount === 1 ? "" : "s"}`);
    }
    parts.push("read-only preview");
    return parts.join(" · ");
  };

  return (
    <div className="mx-auto max-w-[92rem] px-3 py-8 md:px-4 lg:px-5 pb-28 md:pb-8">
      {/* Desktop: top-of-page sticky CTA. Mobile: bottom of page (see below). */}
      <div className="hidden md:flex items-center justify-between gap-3 rounded-2xl frost-panel px-5 py-3 mb-6 sticky top-3 z-20">
        <div className="text-sm text-ink-light">
          Someone shared this trip with you.
        </div>
        {renderCTA()}
      </div>

      <div className="rounded-3xl frost-panel px-6 py-6 md:px-8">
        <h1 className="text-3xl font-black text-ink">
          {trip.title || trip.destination || "Travel Itinerary"}
        </h1>
        {trip.destination && <p className="mt-1 text-ink-light">{trip.destination}</p>}
        {trip.startDate && trip.endDate && (
          <p className="mt-1 text-sm text-ink-light/80">
            {trip.startDate} to {trip.endDate}
          </p>
        )}
        <p className="mt-2 text-xs text-ink-light/80">{authorByline()}</p>

        {trip.parent?.authorName && (
          <p className="mt-2 text-xs text-ink-light">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2.5 py-1">
              <span aria-hidden="true">↪</span>
              <span>
                Based on a trip by{" "}
                <span className="text-ink">{trip.parent.authorName}</span>
              </span>
            </span>
          </p>
        )}
      </div>

      <div className="mt-6 rounded-3xl frost-panel p-4">
        <ItineraryMap
          itinerary={trip.itinerary}
          googleMapsApiKey={googleMapsApiKey}
          onItemClick={handleMarkerClick}
          focusedItemId={focusedItemId}
          selectedItemId={selectedItemId}
        />
      </div>

      <div className="mt-6 rounded-3xl frost-panel p-4 md:p-6">
        <ItineraryView
          itinerary={trip.itinerary}
          highlightedItemId={selectedItemId}
          onItemFocus={handleItemFocus}
          onItemSelect={handleItemSelect}
        />
      </div>

      <div className="mt-6 text-center text-sm text-ink-light">
        Created with{" "}
        <a href="/" className="text-accent hover:underline">
          roam.
        </a>
      </div>

      {/* Mobile: sticky bottom CTA, always reachable while scrolling. */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-deep/95 backdrop-blur-xl border-t border-white/15 px-3 py-3 safe-bottom flex items-center justify-center">
        {renderCTA()}
      </div>

      {dialogOpen && token && (
        <SaveSharedDialog
          token={token}
          source={trip}
          onClose={() => setDialogOpen(false)}
          onSaved={handleSaved}
          onConflict={(existingId) => {
            setDialogOpen(false);
            navigate(`/plan/${existingId}`);
          }}
        />
      )}
    </div>
  );
}
